import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  IconButton
} from '@mui/material';
import { Favorite as FavoriteIcon, Chat as ChatIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, deleteDoc, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase/config';

interface LikedDress {
  id: string;
  dressId: string;
  name: string;
  description: string;
  size: string;
  price: number;
  imageURL: string;
  ownerName: string;
}

const LikedTab: React.FC = () => {
  const { user } = useAuth();
  const [likedDresses, setLikedDresses] = useState<LikedDress[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLikedDresses();
  }, [user]);

  const loadLikedDresses = async () => {
    if (!user) return;
    try {
      const q = query(collection(db, 'likes'), where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      const likedDressesData = await Promise.all(
        querySnapshot.docs.map(async (likeDoc) => {
          const dressDoc = await getDoc(doc(db, 'dresses', likeDoc.data().dressId));
          const dressData = dressDoc.data();
          if (!dressData) return null;
          
          const ownerDoc = await getDoc(doc(db, 'users', dressData.userId));
          const ownerData = ownerDoc.data();

          return {
            id: likeDoc.id,
            dressId: likeDoc.data().dressId,
            name: dressData.name,
            description: dressData.description,
            size: dressData.size,
            price: dressData.price,
            imageURL: dressData.imageURL,
            ownerName: ownerData?.profile?.displayName || 'Propriétaire'
          };
        })
      );
      setLikedDresses(likedDressesData.filter((dress): dress is LikedDress => dress !== null));
    } catch (error) {
      console.error('Error loading liked dresses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlike = async (likeId: string) => {
    if (!user) return;
    try {
      await deleteDoc(doc(db, 'likes', likeId));
      setLikedDresses(prev => prev.filter(dress => dress.id !== likeId));
    } catch (error) {
      console.error('Error removing like:', error);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 4, color: '#C9B6AB' }}>
        Mes Robes Favorites
      </Typography>

      {likedDresses.length === 0 ? (
        <Typography variant="body1" sx={{ textAlign: 'center', color: 'text.secondary' }}>
          Vous n'avez pas encore de robes favorites
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {likedDresses.map((dress) => (
            <Grid item xs={12} sm={6} md={4} key={dress.id}>
              <Card>
                <CardMedia
                  component="img"
                  height="200"
                  image={dress.imageURL}
                  alt={dress.name}
                />
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {dress.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {dress.description}
                  </Typography>
                  <Typography variant="body2">
                    Taille: {dress.size}
                  </Typography>
                  <Typography variant="body2">
                    Prix: {dress.price}€/jour
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Propriétaire: {dress.ownerName}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button
                    variant="contained"
                    startIcon={<ChatIcon />}
                    sx={{
                      backgroundColor: '#C9B6AB',
                      '&:hover': {
                        backgroundColor: '#C1A08E',
                      },
                    }}
                  >
                    Contacter
                  </Button>
                  <IconButton
                    color="error"
                    onClick={() => handleUnlike(dress.id)}
                  >
                    <FavoriteIcon />
                  </IconButton>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default LikedTab; 